import React, { forwardRef, useEffect, useRef, useState } from "react";
import { TitleBar } from "../../Components/Common/TitleBar/titleBar";
import {
  Button,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap";
import "./style/index.scss";
import { useDispatch, useSelector } from "react-redux";
import Plus from "../../assets/images/svg/plus.svg";
import Trash from "../../assets/images/svg/trash-2.svg";
import {
  createMaintenance,
  getAllMaintenanceList,
  getAllVehicleList,
  getAllUsersList,
  getMaintenanceCategory,
  getInsuranceCategoryListApi,
  createInsurance,
} from "../../slices/thunks";
import { TripModal } from "../../Components/Common/TripModal/tripModal";
import { hideErrorMessage } from "../../slices/maintenance/reducer";
import * as Yup from "yup";
import { FieldArray, Formik } from "formik";
import ResponsiveTabs from "../../Components/Common/ResponsiveTabs";
import MaintenanceTable from "./MaintenanceTable";
import InsuranceTable from "./InsuranceTable";
import DatePicker from "react-datepicker";
import { getAllInsurances } from "../../slices/insurance/thunk";

const tabs = [
  { id: "1", label: "Maintenance" },
  { id: "2", label: "Insurance" },
];

const Maintenance = () => {
  const formikRef = useRef(null);
  const fileInputRefs = useRef([]);
  const dispatch = useDispatch();

  const loadingType = useSelector((state) => state.Maintenance.loading);
  const loading = useSelector((state) => state.Insurance.loading);
  const vehicleList = useSelector((state) => state.Vehicle.vehicleList);
  const categoryList = useSelector(
    (state) => state.MaintenanceCategory.maintenanceCategoryList
  );
  const insuranceCategoryList = useSelector(
    (state) => state.InsuranceCategory.insuranceCategoryList
  );

  const [addMaintenanceModal, setAddMaintenanceModal] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toasterType, setToasterType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("1");
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipIndex, setTooltipIndex] = useState(null);

  const [isTooltipInsuranceOpen, setIsTooltipInsuranceOpen] = useState(false);
  const [tooltipIndexInsurance, setTooltipIndexInsurance] = useState(null);

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => {
      setShowToast(false);
      if (!isSuccess) {
        dispatch(hideErrorMessage());
      }
    }, 3000);
  };
  const toggleAddMaintenanceModal = () => {
    setAddMaintenanceModal(!addMaintenanceModal);
  };

  const toggleTooltip = (index) => {
    if (tooltipIndex === index && isTooltipOpen) {
      setIsTooltipOpen(false);
      setTooltipIndex(null);
    } else {
      setIsTooltipOpen(true);
      setTooltipIndex(index);
    }
  };

  const toggleTooltipInsurance = (index) => {
    if (tooltipIndexInsurance === index && isTooltipInsuranceOpen) {
      setIsTooltipInsuranceOpen(false);
      setTooltipIndexInsurance(null);
    } else {
      setIsTooltipInsuranceOpen(true);
      setTooltipIndexInsurance(index);
    }
  };

  const maintenanceSchema = Yup.object().shape({
    vehicle: Yup.string().required("Vehicle is required"),
    maintenance: Yup.array()
      .min(1, "At least one maintenance item is required")
      .of(
        Yup.object().shape({
          vehicle_maintenance_type: Yup.string().required("Type is required"),
          cost: Yup.string().required("Cost is required"),
          performed_on: Yup.string().required("Performed On is required"),
          next_due_date: Yup.string().required("Next Due Date is required"),
          notes: Yup.string().required("Notes is required"),
        })
      ),
  });

  const insuranceSchema = Yup.object().shape({
    vehicle: Yup.string().required("Vehicle is required"),
    insurances: Yup.array()
      .min(1, "At least one insurance item is required")
      .of(
        Yup.object().shape({
          vehicle_maintenance_type: Yup.string().required("Type is required"),
          cost: Yup.string().required("Cost is required"),
          performed_on: Yup.string().required("Performed On is required"),
          next_due_date: Yup.string().required("Next Due Date is required"),
          notes: Yup.string().required("Notes is required"),
        })
      ),
  });

  const addMaintenanceInitialValues = {
    vehicle: "",
    maintenance: [
      {
        vehicle_maintenance_type: "",
        cost: "",
        performed_on: "",
        next_due_date: "",
        notes: "",
        file_uploads: [],
      },
    ],
  };

  const addInsuranceInitialValues = {
    vehicle: "",
    insurance: [
      {
        vehicle_insurance_type: "",
        cost: "",
        performed_on: "",
        next_due_date: "",
        notes: "",
        file_uploads: [],
      },
    ],
  };

  const handleMaintenanceSubmit = async (values) => {
    try {
      let response;
      if (isAdd) {
        response = await dispatch(createMaintenance(values));
      }
      const { success: isSuccess, message } = response;
      showToaster(isSuccess, message);
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      toggleAddMaintenanceModal();
      dispatch(getAllMaintenanceList(currentPage, pageSize, searchText));
    }
  };

  const handleInsuranceSubmit = async (values) => {
    try {
      let response;
      if (isAdd) {
        response = await dispatch(createInsurance(values));
      }
      const { success: isSuccess, message } = response;
      showToaster(isSuccess, message);
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      toggleAddMaintenanceModal();
      dispatch(getAllInsurances(currentPage, pageSize, searchText));
    }
  };

  useEffect(() => {
    try {
      dispatch(getAllMaintenanceList(currentPage, pageSize, searchText));
      dispatch(getAllInsurances(currentPage, pageSize, searchText));
    } catch (error) {
      console.log("error", error);
    }
  }, [dispatch, currentPage, pageSize, searchText]);

  useEffect(() => {
    try {
      const getDetails = async () => {
        try {
          await dispatch(getAllUsersList("None", "None", ""));
          await dispatch(getAllVehicleList("None", "None", ""));
        } catch (error) { }
      };
      getDetails();
    } catch (error) { }
  }, [dispatch]);

  useEffect(() => {
    try {
      dispatch(getMaintenanceCategory("", "", ""));
      dispatch(getInsuranceCategoryListApi("", "", ""));
    } catch (error) { }
  }, [dispatch]);

  document.title = "Maintenance Details | D&A";

  const CustomDateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
    <Input
      vehicle_maintenance_type="text"
      readOnly
      className="custom-date-input"
      onClick={onClick}
      ref={ref}
      value={value}
      placeholder={placeholder}
    />
  ));
  CustomDateInput.displayName = "CustomDateInput";
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-column gap-2 gap-md-4">
            <TitleBar title={"Maintenance & Insurance Table"} />

            <div
              className="position-fixed top-0 end-0 p-3"
              style={{ zIndex: 1055 }}
            >
              <Toast isOpen={showToast}>
                <ToastHeader
                  icon={toasterType === "danger" ? "danger" : "success"}
                  toggle={() => setShowToast(false)}
                >
                  {toasterType === "danger" ? "Error" : "Success"}
                </ToastHeader>
                <ToastBody>{toastMessage}</ToastBody>
              </Toast>
            </div>

            <div className="maintaince-table-header d-flex flex-column-reverse flex-sm-row justify-content-between align-items-end p-2 p-lg-0 gap-1 gap-lg-0">
              <ResponsiveTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabs={tabs}
                isResponsive={false}
              />
              <div className="d-flex maintaince-table-subheader">
                <InputGroup
                  className="search-input"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      dispatch(getAllMaintenanceList(1, pageSize, searchText));
                      setCurrentPage(1);
                    }
                  }}
                >
                  <InputGroupText>
                    <i className="ri-search-line" />
                  </InputGroupText>
                  <Input
                    vehicle_maintenance_type="text"
                    placeholder="Search input text"
                  />
                </InputGroup>
                <Button
                  onClick={async () => {
                    setIsAdd(true);
                    toggleAddMaintenanceModal();
                  }}
                  className="newMaintenanceBtn d-flex gap-1"
                >
                  <span className="d-none d-lg-flex">
                    {activeTab === "1" ? "Add Maintenance" : "Add Insurance"}
                  </span>
                  <i className="ri-add-line" />
                </Button>
              </div>
            </div>
            {activeTab === "1" ? (
              <MaintenanceTable searchText={searchText} />
            ) : (
              <InsuranceTable searchText={searchText} />
            )}
          </div>

          <TripModal
            isModalOpen={addMaintenanceModal}
            toggleModal={toggleAddMaintenanceModal}
            buttonText={activeTab === "1" ? "Add Maintenance" : "Add Insurance"}
            closeText={"Close without saving"}
            title={"Maintenance Management"}
            tripText={
              activeTab === "1"
                ? "Add Maintenance Detail"
                : "Add Insurance Detail"
            }
            showcloseIcon={true}
            size="xl"
            loading={activeTab === "1" ? loadingType.create : loading.create}
            onSubmit={() => {
              if (formikRef.current) {
                formikRef.current.handleSubmit();
              }
            }}
          >
            <div className="px-3 d-flex flex-column gap-4">
              <Formik
                key={activeTab}
                innerRef={formikRef}
                enableReinitialize
                initialValues={
                  activeTab === "1"
                    ? addMaintenanceInitialValues
                    : addInsuranceInitialValues
                }
                validationSchema={
                  activeTab === "1" ? maintenanceSchema : insuranceSchema
                }
                onSubmit={
                  activeTab === "1"
                    ? handleMaintenanceSubmit
                    : handleInsuranceSubmit
                }
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row className="mb-4">
                      <Col md={6}>
                        <FormGroup>
                          <Label for="vehicle" className="passwordLabel">
                            Vehicle Number/ID
                          </Label>
                          <Input
                            type="select"
                            name="vehicle"
                            className="profileInput"
                            id="vehicle"
                            value={values.vehicle}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={
                              touched.vehicle && errors.vehicle ? true : false
                            }
                          >
                            <option value="">Select Vehicle Number/ID</option>
                            {vehicleList?.length &&
                              vehicleList.map((ele) => (
                                <option key={ele.id} value={ele.id}>
                                  {ele.number_plate}
                                </option>
                              ))}
                          </Input>
                          {touched.vehicle && errors.vehicle && (
                            <FormFeedback vehicle_maintenance_type="invalid">
                              {errors.vehicle}
                            </FormFeedback>
                          )}
                        </FormGroup>
                      </Col>

                      {activeTab === "1" ? (
                        <FieldArray name="maintenance">
                          {({ push, remove }) => (
                            <div>
                              <span className="maintenanaceTitle">
                                Maintenance
                              </span>
                              {values.maintenance.map((_, index) => (
                                <Row key={index} className=" mb-4">
                                  <Col xs={12} lg={6} xl={2}>
                                    <FormGroup>
                                      <Label
                                        for={`maintenance[${index}].vehicle_maintenance_type`}
                                        className="passwordLabel"
                                      >
                                        Type
                                      </Label>
                                      <Input
                                        type="select"
                                        name={`maintenance[${index}].vehicle_maintenance_type`}
                                        className="profileInput"
                                        id={`maintenance[${index}].vehicle_maintenance_type`}
                                        value={
                                          values.maintenance[index]
                                            .vehicle_maintenance_type
                                        }
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        invalid={
                                          touched.maintenance?.[index]
                                            ?.vehicle_maintenance_type &&
                                          errors.maintenance?.[index]
                                            ?.vehicle_maintenance_type
                                        }
                                      >
                                        <option value="">Select Type</option>
                                        {categoryList?.results?.map(
                                          (category) => (
                                            <option
                                              key={category.id}
                                              value={category.id}
                                            >
                                              {category.name}
                                            </option>
                                          )
                                        )}
                                      </Input>
                                      {touched.maintenance?.[index]
                                        ?.vehicle_maintenance_type &&
                                        errors.maintenance?.[index]
                                          ?.vehicle_maintenance_type && (
                                          <FormFeedback vehicle_maintenance_type="invalid">
                                            {
                                              errors.maintenance?.[index]
                                                ?.vehicle_maintenance_type
                                            }
                                          </FormFeedback>
                                        )}
                                    </FormGroup>
                                  </Col>
                                  <Col xs={12} lg={6} xl={2}>
                                    <FormGroup>
                                      <Label
                                        for={`maintenance[${index}].cost`}
                                        className="passwordLabel"
                                      >
                                        Cost
                                      </Label>
                                      <Input
                                        type="text"
                                        name={`maintenance[${index}].cost`}
                                        className="profileInput"
                                        id={`maintenance[${index}].cost`}
                                        placeholder="Enter Cost"
                                        value={values.maintenance[index].cost}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        invalid={
                                          touched.maintenance?.[index]?.cost &&
                                          errors.maintenance?.[index]?.cost
                                        }
                                      />
                                      {touched.maintenance?.[index]?.cost &&
                                        errors.maintenance?.[index]?.cost && (
                                          <FormFeedback cost="invalid">
                                            {errors.maintenance?.[index]?.cost}
                                          </FormFeedback>
                                        )}
                                    </FormGroup>
                                  </Col>
                                  <Col xs={12} lg={6} xl={2}>
                                    <FormGroup>
                                      <Label
                                        for={`maintenance[${index}].performed_on`}
                                        className="passwordLabel"
                                      >
                                        Performed On
                                      </Label>
                                      <DatePicker
                                        popperPlacement="right"
                                        selected={
                                          values.maintenance[index].performed_on
                                            ? new Date(
                                              values.maintenance[
                                                index
                                              ].performed_on
                                            )
                                            : null
                                        }
                                        onChange={(date) =>
                                          setFieldValue(
                                            `maintenance[${index}].performed_on`,
                                            date
                                          )
                                        }
                                        dateFormat="MMM dd, yyyy"
                                        placeholderText="Select Date"
                                        customInput={<CustomDateInput />}
                                      />
                                      {touched.maintenance?.[index]
                                        ?.performed_on &&
                                        errors.maintenance?.[index]
                                          ?.performed_on && (
                                          <FormFeedback performed_on="invalid">
                                            {
                                              errors.maintenance?.[index]
                                                ?.performed_on
                                            }
                                          </FormFeedback>
                                        )}
                                    </FormGroup>
                                  </Col>
                                  <Col xs={12} lg={6} xl={2}>
                                    <FormGroup>
                                      <Label
                                        for={`maintenance[${index}].next_due_date`}
                                        className="passwordLabel"
                                      >
                                        Next Due Date
                                      </Label>
                                      <DatePicker
                                        popperPlacement="right"
                                        selected={
                                          values.maintenance[index]
                                            .next_due_date
                                            ? new Date(
                                              values.maintenance[
                                                index
                                              ].next_due_date
                                            )
                                            : null
                                        }
                                        onChange={(date) =>
                                          setFieldValue(
                                            `maintenance[${index}].next_due_date`,
                                            date
                                          )
                                        }
                                        dateFormat="MMM dd, yyyy"
                                        placeholderText="Select Date"
                                        customInput={<CustomDateInput />}
                                      />
                                      {touched.maintenance?.[index]
                                        ?.next_due_date &&
                                        errors.maintenance?.[index]
                                          ?.next_due_date && (
                                          <FormFeedback next_due_date="invalid">
                                            {
                                              errors.maintenance?.[index]
                                                ?.next_due_date
                                            }
                                          </FormFeedback>
                                        )}
                                    </FormGroup>
                                  </Col>
                                  <Col xs={12} lg={6} xl={2}>
                                    <FormGroup>
                                      <Label
                                        for={`maintenance[${index}].notes`}
                                        className="passwordLabel"
                                      >
                                        Notes
                                      </Label>
                                      <Input
                                        type="text"
                                        name={`maintenance[${index}].notes`}
                                        className="profileInput"
                                        id={`maintenance[${index}].notes`}
                                        placeholder="Enter Notes"
                                        value={values.maintenance[index].notes}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        invalid={
                                          touched.maintenance?.[index]?.notes &&
                                          errors.maintenance?.[index]?.notes
                                        }
                                      />
                                      {touched.maintenance?.[index]?.notes &&
                                        errors.maintenance?.[index]?.notes && (
                                          <FormFeedback notes="invalid">
                                            {errors.maintenance?.[index]?.notes}
                                          </FormFeedback>
                                        )}
                                    </FormGroup>
                                  </Col>
                                  <Col xs={12} lg={6} xl={2}>
                                    <div className="d-flex mt-0 mt-lg-4 gap-2">
                                      <Input
                                        type="file"
                                        innerRef={(el) =>
                                          (fileInputRefs.current[index] = el)
                                        }
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (file) {
                                            const currentUploads =
                                              values.maintenance[index]
                                                .file_uploads || [];
                                            const updatedFiles = [
                                              ...currentUploads,
                                              file,
                                            ];
                                            setFieldValue(
                                              `maintenance[${index}].file_uploads`,
                                              updatedFiles
                                            );
                                          }
                                        }}
                                        style={{ display: "none" }}
                                        accept="image/*"
                                      />
                                      <div className="upload-button-wrapper" style={{ position: "relative" }}>
                                        <Button
                                          className="updateProfile updateProfileInline"
                                          onClick={() => fileInputRefs.current[index]?.click()}
                                        >
                                          Upload <i className="ri-upload-2-line"></i>
                                        </Button>

                                        {values.maintenance[index].file_uploads?.length > 0 && (
                                          <span
                                            className="top-badge"
                                            onClick={() => toggleTooltip(index)}
                                            style={{ cursor: "pointer" }}
                                          >
                                            {values.maintenance[index].file_uploads.length}
                                          </span>
                                        )}

                                        {isTooltipOpen && tooltipIndex === index && (
                                          <div className="tooltip-modal" style={{
                                            position: "absolute",
                                            top: "-150px",
                                            right: 0,
                                            background: "white",
                                            border: "1px solid #ccc",
                                            padding: "10px",
                                            zIndex: 1000,
                                            width: "250px",
                                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)"
                                          }}>
                                            <h6>Uploaded Files</h6>
                                            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                              {values.maintenance[index].file_uploads.map((file, fileIdx) => (
                                                <li
                                                  key={fileIdx}
                                                  style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}
                                                >
                                                  <span>{file.name || `File ${fileIdx + 1}`}</span>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const updated = values.maintenance[index].file_uploads.filter(
                                                        (_, i) => i !== fileIdx
                                                      );
                                                      setFieldValue(`maintenance.${index}.file_uploads`, updated);
                                                      setTooltipIndex(null);
                                                      setIsTooltipOpen(false);
                                                    }}
                                                    style={{
                                                      background: "transparent",
                                                      border: "none",
                                                      color: "red",
                                                      cursor: "pointer",
                                                    }}
                                                    aria-label={`Remove ${file.name || `File ${fileIdx + 1}`}`}
                                                  >
                                                    <i className="ri-delete-bin-line text-danger"></i>                                                  </button>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </div>

                                      <>
                                        {index ===
                                          values.maintenance.length - 1 && (
                                            <i
                                              className="cursor-pointer"
                                              style={{
                                                fontSize: "20px",
                                                margin: "0px",
                                              }}
                                              onClick={() =>
                                                push({
                                                  vehicle_maintenance_type: "",
                                                  cost: "",
                                                  performed_on: null,
                                                  next_due_date: null,
                                                  notes: "",
                                                })
                                              }
                                            >
                                              <img
                                                src={Plus}
                                                alt="Plus icon"
                                                style={{
                                                  width: "24px",
                                                  height: "24px",
                                                }}
                                              />
                                            </i>
                                          )}
                                        {values.maintenance.length > 1 && (
                                          <i
                                            className="cursor-pointer"
                                            style={{ fontSize: "20px" }}
                                            onClick={() => remove(index)}
                                          >
                                            <img
                                              src={Trash}
                                              alt="Trash icon"
                                              style={{
                                                width: "20px",
                                                height: "20px",
                                                color: "#667085",
                                              }}
                                            />
                                          </i>
                                        )}
                                      </>
                                    </div>
                                  </Col>
                                </Row>
                              ))}
                            </div>
                          )}
                        </FieldArray>
                      ) : activeTab === "2" ? (
                        <FieldArray name="insurance">
                          {({ push, remove }) => (
                            <div>
                              <span className="maintenanaceTitle">
                                Insurances
                              </span>
                              {values.insurance.map((_, index) => (
                                <Row key={index} className="mb-2">
                                  <Col xs={12} lg={6} xl={2}>
                                    <FormGroup>
                                      <Label
                                        for={`insurance[${index}].vehicle_insurance_type`}
                                        className="passwordLabel"
                                      >
                                        Type
                                      </Label>
                                      <Input
                                        type="select"
                                        name={`insurance[${index}].vehicle_insurance_type`}
                                        className="profileInput"
                                        id={`insurance[${index}].vehicle_insurance_type`}
                                        value={
                                          values.insurance[index]
                                            .vehicle_insurance_type
                                        }
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        invalid={
                                          touched.insurance?.[index]
                                            ?.vehicle_insurance_type &&
                                          !!errors.insurance?.[index]
                                            ?.vehicle_insurance_type
                                        }
                                      >
                                        <option value="">Select Type</option>
                                        {insuranceCategoryList?.results?.map(
                                          (category) => (
                                            <option
                                              key={category.id}
                                              value={category.id}
                                            >
                                              {category.name}
                                            </option>
                                          )
                                        )}
                                      </Input>
                                      {touched.insurance?.[index]
                                        ?.vehicle_insurance_type &&
                                        errors.insurance?.[index]
                                          ?.vehicle_insurance_type && (
                                          <FormFeedback vehicle_insurance_type="invalid">
                                            {
                                              errors.insurance?.[index]
                                                ?.vehicle_insurance_type
                                            }
                                          </FormFeedback>
                                        )}
                                    </FormGroup>
                                  </Col>

                                  <Col xs={12} lg={6} xl={2}>
                                    {" "}
                                    <FormGroup>
                                      <Label
                                        for={`insurance[${index}].cost`}
                                        className="passwordLabel"
                                      >
                                        Cost
                                      </Label>
                                      <Input
                                        type="text"
                                        name={`insurance[${index}].cost`}
                                        className="profileInput"
                                        id={`insurance[${index}].cost`}
                                        placeholder="Enter Cost"
                                        value={values.insurance[index].cost}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        invalid={
                                          touched.insurance?.[index]?.cost &&
                                          errors.insurance?.[index]?.cost
                                        }
                                      />
                                      {touched.insurance?.[index]?.cost &&
                                        errors.insurance?.[index]?.cost && (
                                          <FormFeedback cost="invalid">
                                            {errors.insurance?.[index]?.cost}
                                          </FormFeedback>
                                        )}
                                    </FormGroup>
                                  </Col>

                                  <Col xs={12} lg={6} xl={2}>
                                    {" "}
                                    <FormGroup>
                                      <Label
                                        for={`insurance[${index}].performed_on`}
                                        className="passwordLabel"
                                      >
                                        Performed On
                                      </Label>
                                      <DatePicker
                                        popperPlacement="right"
                                        selected={
                                          values.insurance[index].performed_on
                                            ? new Date(
                                              values.insurance[
                                                index
                                              ].performed_on
                                            )
                                            : null
                                        }
                                        onChange={(date) =>
                                          setFieldValue(
                                            `insurance[${index}].performed_on`,
                                            date
                                          )
                                        }
                                        dateFormat="MMM dd, yyyy"
                                        placeholderText="Select Date"
                                        customInput={<CustomDateInput />}
                                      />
                                      {touched.insurance?.[index]
                                        ?.performed_on &&
                                        errors.insurance?.[index]
                                          ?.performed_on && (
                                          <FormFeedback performed_on="invalid">
                                            {
                                              errors.insurance?.[index]
                                                ?.performed_on
                                            }
                                          </FormFeedback>
                                        )}
                                    </FormGroup>
                                  </Col>

                                  <Col xs={12} lg={6} xl={2}>
                                    {" "}
                                    <FormGroup>
                                      <Label
                                        for={`insurance[${index}].next_due_date`}
                                        className="passwordLabel"
                                      >
                                        Next Due Date
                                      </Label>
                                      <DatePicker
                                        popperPlacement="right"
                                        selected={
                                          values.insurance[index].next_due_date
                                            ? new Date(
                                              values.insurance[
                                                index
                                              ].next_due_date
                                            )
                                            : null
                                        }
                                        onChange={(date) =>
                                          setFieldValue(
                                            `insurance[${index}].next_due_date`,
                                            date
                                          )
                                        }
                                        dateFormat="MMM dd, yyyy"
                                        placeholderText="Select Date"
                                        customInput={<CustomDateInput />}
                                      />
                                      {touched.insurance?.[index]
                                        ?.next_due_date &&
                                        errors.insurance?.[index]
                                          ?.next_due_date && (
                                          <FormFeedback next_due_date="invalid">
                                            {
                                              errors.insurance?.[index]
                                                ?.next_due_date
                                            }
                                          </FormFeedback>
                                        )}
                                    </FormGroup>
                                  </Col>

                                  <Col>
                                    <FormGroup>
                                      <Label
                                        for={`insurance[${index}].notes`}
                                        className="passwordLabel"
                                      >
                                        Notes
                                      </Label>
                                      <Input
                                        type="text"
                                        name={`insurance[${index}].notes`}
                                        className="profileInput"
                                        id={`insurance[${index}].notes`}
                                        placeholder="Enter Notes"
                                        value={values.insurance[index].notes}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        invalid={
                                          touched.insurance?.[index]?.notes &&
                                          errors.insurance?.[index]?.notes
                                        }
                                      />
                                      {touched.insurance?.[index]?.notes &&
                                        errors.insurance?.[index]?.notes && (
                                          <FormFeedback notes="invalid">
                                            {errors.insurance?.[index]?.notes}
                                          </FormFeedback>
                                        )}
                                    </FormGroup>
                                  </Col>

                                  <Col xs={12} lg={6} xl={2}>
                                    {" "}
                                    <div className="d-flex mt-0 mt-lg-4 gap-2 ">
                                      <Input
                                        type="file"
                                        innerRef={(el) =>
                                          (fileInputRefs.current[index] = el)
                                        }
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (file) {
                                            const currentUploads =
                                              values.insurance[index]
                                                .file_uploads || [];
                                            const updatedFiles = [
                                              ...currentUploads,
                                              file,
                                            ];
                                            setFieldValue(
                                              `insurance[${index}].file_uploads`,
                                              updatedFiles
                                            );
                                          }
                                        }}
                                        style={{ display: "none" }}
                                        accept="image/*"
                                      />
                                      <div className="upload-button-wrapper" style={{ position: "relative" }}>
                                        <Button
                                          className="updateProfile updateProfileInline"
                                          onClick={() => fileInputRefs.current[index]?.click()}
                                        >
                                          Upload <i className="ri-upload-2-line"></i>
                                        </Button>

                                        {values.insurance[index].file_uploads?.length > 0 && (
                                          <span
                                            className="top-badge"
                                            onClick={() => toggleTooltipInsurance(index)}
                                            style={{ cursor: "pointer" }}
                                          >
                                            {values.insurance[index].file_uploads.length}
                                          </span>
                                        )}

                                        {isTooltipInsuranceOpen && tooltipIndexInsurance === index && (
                                          <div className="tooltip-modal" style={{
                                            position: "absolute",
                                            top: "-150px",
                                            right: 0,
                                            background: "white",
                                            border: "1px solid #ccc",
                                            padding: "10px",
                                            zIndex: 1000,
                                            width: "250px",
                                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)"
                                          }}>
                                            <h6>Uploaded Files</h6>
                                            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                              {values.insurance[index].file_uploads.map((file, fileIdx) => (
                                                <li
                                                  key={fileIdx}
                                                  style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}
                                                >
                                                  <span>{file.name || `File ${fileIdx + 1}`}</span>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const updated = values.insurance[index].file_uploads.filter(
                                                        (_, i) => i !== fileIdx
                                                      );
                                                      setFieldValue(`insurance.${index}.file_uploads`, updated);
                                                      setTooltipIndexInsurance(null);
                                                      setIsTooltipInsuranceOpen(false);
                                                    }}
                                                    style={{
                                                      background: "transparent",
                                                      border: "none",
                                                      color: "red",
                                                      cursor: "pointer",
                                                    }}
                                                    aria-label={`Remove ${file.name || `File ${fileIdx + 1}`}`}
                                                  >
                                                    <i className="ri-delete-bin-line text-danger"></i>                                                  </button>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </div>

                                      <>
                                        {index ===
                                          values.insurance.length - 1 && (
                                            <i
                                              className="cursor-pointer"
                                              style={{
                                                fontSize: "20px",
                                                margin: "0px",
                                              }}
                                              onClick={() =>
                                                push({
                                                  vehicle_insurance_type: "",
                                                  cost: "",
                                                  performed_on: null,
                                                  next_due_date: null,
                                                  notes: "",
                                                  file_uploads: [],
                                                })
                                              }
                                            >
                                              <img
                                                src={Plus}
                                                alt="Plus icon"
                                                style={{
                                                  width: "24px",
                                                  height: "24px",
                                                }}
                                              />
                                            </i>
                                          )}
                                        {values.insurance.length > 1 && (
                                          <i
                                            className="cursor-pointer"
                                            style={{ fontSize: "20px" }}
                                            onClick={() => remove(index)}
                                          >
                                            <img
                                              src={Trash}
                                              alt="Trash icon"
                                              style={{
                                                width: "20px",
                                                height: "20px",
                                                color: "#667085",
                                              }}
                                            />
                                          </i>
                                        )}
                                      </>
                                    </div>
                                  </Col>
                                </Row>
                              ))}
                            </div>
                          )}
                        </FieldArray>
                      ) : null}
                    </Row>
                  </Form>
                )}
              </Formik>
            </div>
          </TripModal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Maintenance;
